// frontend/src/app/pages/profile/two-factor-card/two-factor-card.component.ts
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SettingsService } from '../../../services/settings.service';

type Step = 'idle' | 'setup' | 'backup' | 'disable';

/** Double authentification dans « Mon profil » (SEC-06, D-08) : activation, codes de secours, désactivation. */
@Component({
  selector: 'app-two-factor-card',
  imports: [ReactiveFormsModule],
  templateUrl: './two-factor-card.component.html',
})
export class TwoFactorCardComponent {
  private readonly settingsService = inject(SettingsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  /** null tant que le statut n'est pas chargé */
  readonly enabled = signal<boolean | null>(null);
  readonly step = signal<Step>('idle');
  readonly qrCode = signal('');
  readonly secret = signal('');
  readonly backupCodes = signal<string[]>([]);
  readonly copied = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');

  readonly setupForm = this.fb.nonNullable.group({
    code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
  });
  readonly disableForm = this.fb.nonNullable.group({
    password: ['', [Validators.required]],
    code: ['', [Validators.required, Validators.maxLength(20)]],
  });

  constructor() {
    this.settingsService
      .getSettings()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.enabled.set(Boolean(res.data?.twoFactorEnabled)),
        error: () => {
          this.enabled.set(false);
          this.error.set('Statut de la double authentification indisponible.');
        },
      });
  }

  startSetup(): void {
    this.goTo('setup');
    this.busy.set(true);
    this.settingsService
      .enableTwoFactor()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.busy.set(false);
          this.qrCode.set(res.data?.qrCode ?? '');
          this.secret.set(res.data?.secret ?? '');
        },
        error: (err: HttpErrorResponse) =>
          this.fail(err, "Impossible de démarrer l'activation."),
      });
  }

  confirmSetup(): void {
    if (this.setupForm.invalid) {
      this.setupForm.markAllAsTouched();
      return;
    }
    this.busy.set(true);
    this.error.set('');
    this.settingsService
      .verifyTwoFactor(this.setupForm.getRawValue().code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.busy.set(false);
          if (!res.success || !res.data) {
            this.error.set(res.error ?? 'Code invalide.');
            return;
          }
          this.enabled.set(true);
          this.goTo('backup');
          this.backupCodes.set(res.data.backupCodes);
        },
        error: (err: HttpErrorResponse) => this.fail(err, 'Code invalide.'),
      });
  }

  copyBackupCodes(): void {
    navigator.clipboard
      .writeText(this.backupCodes().join('\n'))
      .then(() => this.copied.set(true))
      .catch(() => this.error.set('Copie impossible : notez les codes.'));
  }

  startDisable(): void {
    this.goTo('disable');
  }

  confirmDisable(): void {
    if (this.disableForm.invalid) {
      this.disableForm.markAllAsTouched();
      return;
    }
    const { password, code } = this.disableForm.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.settingsService
      .disableTwoFactor(password, code.trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.enabled.set(false);
          this.goTo('idle');
        },
        error: (err: HttpErrorResponse) =>
          this.fail(err, 'Désactivation impossible.'),
      });
  }

  /** Retour à l'état de repos ; efface les codes de secours affichés. */
  close(): void {
    this.goTo('idle');
  }

  private goTo(step: Step): void {
    this.step.set(step);
    this.error.set('');
    this.qrCode.set('');
    this.secret.set('');
    this.backupCodes.set([]);
    this.copied.set(false);
    this.setupForm.reset();
    this.disableForm.reset();
  }

  private fail(err: HttpErrorResponse, fallback: string): void {
    this.busy.set(false);
    this.error.set(err.error?.message ?? fallback);
  }
}
